/**
 * First off, do not build an HTML parser. It is ridiculously difficult and a
 * waste of time. That said, here is a very simple parser which (hopefully)
 * supports only a handful of HTML tags, attributes, and protocols.
 *
 * Why do we need this?
 *
 * Actions that are built with our AutomationActions v4 APIs allow our users
 * (integrators) the ability to configure and maintain their own I18n strings.
 * This includes descriptions, labels, help texts, and error messages.
 *
 * We support a small subset of their use-cases and should be careful with what
 * we choose to support so we can continue to maintain a consistent UX.
 */

import { clean } from 'automation-ui-action-features/utils/sanitize';
import { DocLink } from 'automation-ui-usage-tracking/components/DocLink';
import { Fragment } from 'react';
import memoize from 'transmute/memoize';
import UILink from 'UIComponents/link/UILink';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
let uniqueKey = 0; // Prevent React unique key warnings.

/**
 * Allowlist of supported elements, their attributes, and protocols.
 */
const sanitizeConfig = {
  elements: ['a', 'p', 'doclink', 'strong'],
  attributes: {
    a: ['href'],
    doclink: ['href']
  },
  protocols: {
    a: {
      href: ['http', 'https']
    },
    doclink: {
      href: ['https']
    }
  }
};

/**
 * Captures <{tag} href="{href}">{body}</{tag}>
 * where {tag} is either "a" or "doclink".
 *
 * "doclink" is a custom element (must be lowercased so it passes the sanitizer)
 * which we use to render a KnowledgeBaseButton. This is required because it
 * supports click tracking.
 */
const linkTagsRegex = /<(?<tag>a|doclink)(?:\s+href="(?<href>[^"]*)"\s*)?>(?<body>.*?)<\/\k<tag>>/g;
const suspectedIPAddressRegex = /^https?:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/;
const getStringsWithLinks = memoize(sanitizedString => {
  const nodes = [];
  let startIndex = 0;
  let execResult = linkTagsRegex.exec(sanitizedString);
  while (execResult) {
    const endIndex = execResult.index;
    if (startIndex !== endIndex) {
      nodes.push( /*#__PURE__*/_jsx(Fragment, {
        children: sanitizedString.substring(startIndex, endIndex)
      }, uniqueKey++));
    }
    const {
      body,
      href,
      tag
    } = execResult.groups || {};
    if (body) {
      const invalidHref = !href || suspectedIPAddressRegex.test(href);
      if (invalidHref) {
        nodes.push( /*#__PURE__*/_jsx(Fragment, {
          children: body
        }, uniqueKey++));
      } else {
        nodes.push(tag === 'doclink' ? /*#__PURE__*/_jsx(DocLink, {
          href: href,
          children: body
        }, uniqueKey++) : /*#__PURE__*/_jsx(View, {
          display: "inline-block",
          children: /*#__PURE__*/_jsx(UILink, {
            external: true,
            href: href,
            children: body
          }, uniqueKey++)
        }));
      }
    }
    const [match] = execResult;
    startIndex = endIndex + match.length;
    execResult = linkTagsRegex.exec(sanitizedString);
  }
  const remainingString = sanitizedString.substring(startIndex, sanitizedString.length);
  if (remainingString) {
    nodes.push( /*#__PURE__*/_jsx(Fragment, {
      children: remainingString
    }, uniqueKey++));
  }
  return nodes;
});

/**
 * Captures <{tag}>{body}</{tag}>
 * where {tag} is "strong" and {body} is anything in between the tag pairs.
 *
 * This is a lazy-regex that will find the first open and close tag pairs. We
 * want to minimize the level of nested tags because we should not be building
 * an HTML parser. See note at the top of this file. This is level 2.
 */
const styleTagsRegex = /<(?<tag>strong)>(?<body>.*?)<\/\k<tag>>/g;
const getStyledTextWithLinks = memoize(sanitizedString => {
  const nodes = [];
  let startIndex = 0;
  let execResult = styleTagsRegex.exec(sanitizedString);
  while (execResult) {
    const endIndex = execResult.index;
    if (startIndex !== endIndex) {
      nodes.push(...getStringsWithLinks(sanitizedString.substring(startIndex, endIndex)));
    }
    const {
      body,
      tag
    } = execResult.groups || {};
    if (body) {
      const stringsWithLinks = getStringsWithLinks(body);
      if (tag === 'strong') {
        nodes.push( /*#__PURE__*/_jsx("strong", {
          children: stringsWithLinks
        }, uniqueKey++));
      } else {
        nodes.push(...stringsWithLinks);
      }
    }
    const [match] = execResult;
    startIndex = endIndex + match.length;
    execResult = styleTagsRegex.exec(sanitizedString);
  }
  const remainingString = sanitizedString.substring(startIndex, sanitizedString.length);
  if (remainingString) {
    nodes.push(...getStringsWithLinks(remainingString));
  }
  return nodes;
});

/**
 * Captures <{tag}>{body}</{tag}>
 * where {tag} is "p" and {body} is anything in between the tag pairs.
 *
 * This is a lazy-regex that will find the first open and close tag pairs. We
 * want to minimize the level of nested tags because we should not be building
 * an HTML parser. See note at the top of this file. This is the top-level.
 */
const blockTagRegex = /<(?<tag>p)>(?<body>.*?)<\/\k<tag>>/g;
const sanitizeWithExternalLinks = memoize(string => {
  if (typeof string !== 'string') return [];
  const sanitizedString = clean(string, sanitizeConfig);
  const nodes = [];
  let startIndex = 0;
  let execResult = blockTagRegex.exec(sanitizedString);
  while (execResult) {
    const endIndex = execResult.index;
    if (startIndex !== endIndex) {
      nodes.push(...getStyledTextWithLinks(sanitizedString.substring(startIndex, endIndex)));
    }
    const {
      body,
      tag
    } = execResult.groups || {};
    if (body) {
      const styledTextWithLink = getStyledTextWithLinks(body);
      if (tag === 'p') {
        nodes.push( /*#__PURE__*/_jsx("p", {
          children: styledTextWithLink
        }, uniqueKey++));
      } else {
        nodes.push(...styledTextWithLink);
      }
    }
    const [match] = execResult;
    startIndex = endIndex + match.length;
    execResult = blockTagRegex.exec(sanitizedString);
  }
  const remainingString = sanitizedString.substring(startIndex, sanitizedString.length);
  if (remainingString) {
    nodes.push(...getStyledTextWithLinks(remainingString));
  }
  return nodes;
});
export default sanitizeWithExternalLinks;